import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import '../../XXX/GameSimulator/GameContainer2/GameGrid/AddChampion.css';
import DataService from '../../XXX/DataService';

interface Champion {
  id: string;
  name: string;
  imageUrl: string;
}

export interface StaticChampionAddData {
  name: string;
  level: number;
  team: 'blue' | 'red';
}

interface StaticAddChampionProps {
  onSelectChampion: (championData: StaticChampionAddData) => void;
  onCancel: () => void;
  defaultTeam?: 'blue' | 'red';
}

const StaticAddChampion: React.FC<StaticAddChampionProps> = ({ 
  onSelectChampion, 
  onCancel, 
  defaultTeam = 'blue' 
}) => {
  const [championsList, setChampionsList] = useState<Champion[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<Champion[]>([]);
  const [selectedChampion, setSelectedChampion] = useState<Champion | null>(null);
  const [previouslySelected, setPreviouslySelected] = useState<string | null>(null);
  const [level, setLevel] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch champions on component mount
  useEffect(() => {
    const fetchChampions = async () => {
      try {
        // Fetch latest version dynamically
        const version = await DataService.fetchLatestVersion();
        
        // Fetch champions
        const response = await fetch(
          `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
        );
        const data = await response.json();

        // Create our standard champion array
        const championsData = Object.values(data.data).map((champion: any) => ({
          id: champion.id,
          name: champion.name,
          imageUrl: `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion.id}.png`,
        }));

        // Append the "Practice Dummy" champion to the list
        championsData.push({
          id: 'DummyChampion',
          name: 'DummyChampion',
          imageUrl:
            'https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png',
        });


        setChampionsList(championsData);
      } catch (error) {
        console.error('Error fetching champions:', error);
      }
    };

    fetchChampions();
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setSearchInput(input);

    if (input.length > 0) {
      // Filter champions based on input
      const filteredChampions = championsList
        .filter((champion) =>
          champion.name.toLowerCase().includes(input.toLowerCase())
        )
        .slice(0, 8);
      setSuggestions(filteredChampions);
      
      // Auto-select first suggestion if available
      if (filteredChampions.length > 0) {
        setSelectedChampion(filteredChampions[0]);
        setPreviouslySelected(null); // Reset previous selection
      } else {
        setSelectedChampion(null);
        setPreviouslySelected(null);
      }
    } else {
      setSuggestions([]);
      setSelectedChampion(null);
      setPreviouslySelected(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && selectedChampion) {
      handleConfirmSelection();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleConfirmSelection = async () => {
    if (selectedChampion) {
      setIsLoading(true);
      
      const championData: StaticChampionAddData = {
        name: selectedChampion.id,  // Send DDragon ID (e.g. "BelVeth") instead of display name (e.g. "Bel'Veth")
        level: level,
        team: defaultTeam // Use the auto-detected team
      };
      
      onSelectChampion(championData);
      setIsLoading(false);
    }
  };

  const handleLevelInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= 18) {
      setLevel(value);
    } else if (e.target.value === '') {
      // Allow empty input temporarily
      setLevel(1);
    }
  };

  const handleChampionSelect = (champion: Champion) => {
    // If this is the currently selected champion, add it (second click)
    if (selectedChampion && selectedChampion.id === champion.id) {
      console.log('[StaticAddChampion] Second click on', champion.name, '- adding champion');
      handleConfirmSelection();
      return;
    }
    
    // Otherwise, select this champion (first click, even if switching from another champion)
    console.log('[StaticAddChampion] First click on', champion.name, '- selecting champion');
    setSelectedChampion(champion);
    setPreviouslySelected(champion.id);
  };

  // Handle double-click on champion name
  const handleChampionNameDoubleClick = () => {
    if (selectedChampion) {
      handleConfirmSelection();
    }
  };

  return (
    <div 
      className='add-champion'
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking anywhere in the component
    >
      <div className='champion-search-container'>
        <h3 className="search-title">Add Champion</h3>
        
        <div 
          className='search-bar'
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking in the search area
        >
          <input
            ref={inputRef}
            type='text'
            placeholder='Search for a champion'
            value={searchInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking the search input
            className="champion-search-input"
          />
          
          {suggestions.length > 0 && (
            <ul className='champion-suggestions-list'>
                {suggestions.map((champion) => (
                  <li
                    key={champion.id}
                    className={`champion-suggestion-item ${selectedChampion?.id === champion.id ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent closing the window
                      handleChampionSelect(champion);
                    }}
                  >
                    <img src={champion.imageUrl} alt={champion.name} />
                    <span>{champion.name}</span>
                    {selectedChampion?.id === champion.id && (
                      <span className="click-again-hint">Click again to add</span>
                    )}
                  </li>
                ))}
            </ul>
          )}
        </div>
        
          {selectedChampion && (
            <div 
              className="champion-selection-panel"
              onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the panel
            >
              <div className="selected-champion-info">
              <img 
                src={selectedChampion.imageUrl} 
                alt={selectedChampion.name} 
                className="selected-champion-image" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmSelection();
                }}
                title="Click to add champion"
              />
              <div className="champion-details">
                <span 
                  className="selected-champion-name"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmSelection();
                  }}
                  onDoubleClick={handleChampionNameDoubleClick}
                  title="Click to add champion"
                >
                  {selectedChampion.name}
                </span>
              </div>
              
              <div 
                className="level-selector"
                onClick={(e) => e.stopPropagation()} // Prevent any action when clicking in this area
              >
                <label htmlFor="level-input">Level:</label>
                <input
                  id="level-input"
                  type="number"
                  min="1"
                  max="18"
                  value={level}
                  onChange={handleLevelInputChange}
                  className="level-input"
                />
              </div>
              
              <div 
                className="team-display"
                onClick={(e) => e.stopPropagation()} // Prevent any action when clicking in this area
              >
                <label>Team:</label>
                <span className={`team-indicator ${defaultTeam}`}>
                  {defaultTeam.charAt(0).toUpperCase() + defaultTeam.slice(1)}
                </span>
              </div>
            </div>
            
            <div className="champion-selection-actions">
              <button 
                className="champion-action-button"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                className="champion-action-button confirm-button"
                onClick={handleConfirmSelection}
                disabled={isLoading}
              >
                {isLoading ? 'Adding...' : 'Add Champion'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaticAddChampion;
